// src/features/withholding_docs/Withholding_docsList.js
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import useTaxCustomer from '../taxcontext/useTaxCustomer'
import CustomerContextSelector from '../taxcontext/CustomerContextSelector'
import {
  useGetWithholding_docsQuery,
  makeWithholding_docSelectors,
} from './withholding_docsApiSlice'
import Withholding_docTable from './components/Withholding_docTable'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

import PajakContextBar from '../taxcontext/PajakContextBar'
import { useGetTaxpayersQuery } from '../taxpayers/taxpayersApiSlice'
import {
  selectCurrentTaxpayerId,
  selectCurrentTaxYear,
} from '../taxcontext/taxContextSlice'

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 200]

export default function Withholding_docsList() {
  const customerId = useTaxCustomer()

  const taxpayer_id = useSelector(selectCurrentTaxpayerId)
  const tax_year = useSelector(selectCurrentTaxYear)

  const {
    currentData: taxpayers,
    isFetching: isLoadingTaxpayers,
    error: taxpayerError,
  } = useGetTaxpayersQuery(
    { customer_id: customerId, limit: 200 },
    { skip: !customerId }
  )

  const [q, setQ] = useState('')
  const [statusBukti, setStatusBukti] = useState('')
  const [sifatPemotongan, setSifatPemotongan] = useState('')
  const [documentView, setDocumentView] = useState('all')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const params = useMemo(() => {
    const p = {
      customer_id: customerId,
      sortBy,
      sortDir,
      tax_year,
      taxpayer_id,
      document_view: documentView,
    }

    if (q.trim()) p.q = q.trim()
    if (statusBukti) p.status_bukti_pemotongan = statusBukti
    if (sifatPemotongan) p.sifat_pemotongan = sifatPemotongan

    return p
  }, [
    customerId,
    q,
    statusBukti,
    sifatPemotongan,
    documentView,
    sortBy,
    sortDir,
    tax_year,
    taxpayer_id,
  ])

  const queryArgs = useMemo(
    () => ({ ...params, page, limit }),
    [params, page, limit]
  )

  useEffect(() => { setPage(1) }, [customerId, taxpayer_id, tax_year])

  const skipQuery = !customerId || !tax_year || !taxpayer_id

  const { currentData: data, isLoading, isFetching, isError, error } =
    useGetWithholding_docsQuery(queryArgs, {
      skip: skipQuery,
      // pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    })

  const { selectAllWithholding_docs, selectTotal } = useMemo(
    () => makeWithholding_docSelectors(queryArgs),
    [queryArgs]
  )

  const withholding_docs = useSelector(selectAllWithholding_docs)
  const total = useSelector(selectTotal)

  const totalPages = Math.max(1, Math.ceil((total || 0) / limit))
  const from = total === 0 ? 0 : (page - 1) * limit + 1
  const to = Math.min(page * limit, total || 0)

  const pageIsFull = (data?.ids?.length || 0) >= limit
  const canPrev = page > 1
  const canNext = totalPages > 1 ? page < totalPages : pageIsFull

  const onPrev = () => {
    if (canPrev) setPage((prev) => prev - 1)
  }

  const onNext = () => {
    if (canNext) setPage((prev) => prev + 1)
  }

  const onPageSizeChange = (e) => {
    setLimit(Number(e.target.value))
    setPage(1)
  }

  const loading = isLoading || isFetching

  return (
    <section className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            Daftar Bukti Potong
          </h1>
          <p className="text-sm text-gray-500">
            Manage Bukti Potong (withholding) document records.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            customer_id: {customerId || '-'}
          </p>
        </div>

        <Link
          to="/dash/withholding_docs/new"
          className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
        >
          New Withholding Doc
        </Link>
      </header>

      <CustomerContextSelector />
      {taxpayerError && (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {taxpayerError?.data?.message || 'Gagal memuat wajib pajak.'}
        </div>
      )}
      <PajakContextBar
        taxpayers={taxpayers}
        isLoadingTaxpayers={isLoadingTaxpayers}
        onApply={() => {
          setPage(1)
        }}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setPage(1)
          }}
          placeholder="Search doc number / payer / doc id..."
          className="w-72 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
        />

        <select
          value={documentView}
          onChange={(e) => {
            setDocumentView(e.target.value)
            setPage(1)
          }}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="all">All Documents</option>
          <option value="valid_only">Valid Documents</option>
        </select>

        <select
          value={statusBukti}
          onChange={(e) => {
            setStatusBukti(e.target.value)
            setPage(1)
          }}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">All status bukti</option>
          <option value="NORMAL">NORMAL</option>
          <option value="PEMBETULAN">PEMBETULAN</option>
          <option value="DIBATALKAN">DIBATALKAN</option>
        </select>

        <select
          value={sifatPemotongan}
          onChange={(e) => {
            setSifatPemotongan(e.target.value)
            setPage(1)
          }}
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
        >
          <option value="">All sifat</option>
          <option value="FINAL">FINAL</option>
          <option value="TIDAK FINAL">TIDAK FINAL</option>
        </select>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value)
              setPage(1)
            }}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="created_at">Created</option>
            <option value="doc_date">Doc Date</option>
            <option value="payer_name">Payer Name</option>
            <option value="gross_income">Gross Income</option>
            <option value="tax_withheld">Tax Withheld</option>
            <option value="tax_year">Tax Year</option>
            <option value="withholding_doc_id">Doc ID</option>
          </select>

          <select
            value={sortDir}
            onChange={(e) => {
              setSortDir(e.target.value)
              setPage(1)
            }}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>
      </div>

      {!skipQuery && isError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error?.data?.message || 'Failed to load withholding docs.'}
        </div>
      )}

      {skipQuery ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          Pilih customer, wajib pajak, dan tahun pajak, lalu klik Apply.
        </p>
      ) : !isError && (
        <Withholding_docTable withholding_docs={withholding_docs} loading={loading} />
      )}

      <div className="flex items-center justify-between text-sm text-neutral-600">
        <div>
          {loading
            ? 'Loading...'
            : `${from}–${to} of ${total?.toLocaleString?.() ?? total}`}
        </div>

        <div className="flex items-center gap-3">
          <label className="mr-1 text-sm text-neutral-500">Rows:</label>
          <select
            value={limit}
            onChange={onPageSizeChange}
            className="rounded-xl border border-neutral-300 bg-white px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={onPrev}
              disabled={!canPrev}
              className={`flex h-9 w-9 items-center justify-center rounded-full border border-neutral-300 transition ${
                canPrev
                  ? 'hover:bg-neutral-100 active:scale-[0.98]'
                  : 'cursor-not-allowed opacity-40'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4 text-neutral-700" />
            </button>

            <button
              onClick={onNext}
              disabled={!canNext}
              className={`flex h-9 w-9 items-center justify-center rounded-full border border-neutral-300 transition ${
                canNext
                  ? 'hover:bg-neutral-100 active:scale-[0.98]'
                  : 'cursor-not-allowed opacity-40'
              }`}
            >
              <ChevronRightIcon className="h-4 w-4 text-neutral-700" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}